"use client";

import { lazy, Suspense, useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { MessageSquare } from "@/components/icons";
import { RepackifyMark } from "@/components/brand/repackify-logo";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { Button } from "@/components/ui/button";
import { LoadFailureDialog } from "@/components/ui/load-failure-dialog";
import { useSearchContextSafe } from "@/components/search/search-context";
import { useActiveViewAs } from "@/components/view-as/use-active-view-as";
import { ASK_PANEL_HIDDEN_KEY, ASK_PANEL_OPEN_QUERY } from "@/lib/chat/ask-panel-flag";
import { OPEN_SUPPORT_CHAT_EVENT } from "@/lib/chat/open-public-chat";

const PublicAskPanel = lazy(() =>
  import("./public-ask-panel").then((module) => ({
    default: module.PublicAskPanel,
  })),
);


function readHiddenPreference(): boolean {
  try {
    return window.localStorage.getItem(ASK_PANEL_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeHiddenPreference(hidden: boolean) {
  try {
    if (hidden) window.localStorage.setItem(ASK_PANEL_HIDDEN_KEY, "1");
    else window.localStorage.removeItem(ASK_PANEL_HIDDEN_KEY);
  } catch {
    return;
  }
}

function subscribeToWidth(onChange: () => void): () => void {
  const query = window.matchMedia(ASK_PANEL_OPEN_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function readOpenByDefault(): boolean {
  return window.matchMedia(ASK_PANEL_OPEN_QUERY).matches && !readHiddenPreference();
}

function subscribeToNothing(): () => void {
  return () => undefined;
}

function useAskPanelRootFlag(visible: boolean, threadWanted: boolean) {
  const hydrated = useSyncExternalStore(subscribeToNothing, () => true, () => false);
  useEffect(() => {
    if (!hydrated) return;
    const root = document.documentElement;
    if (visible) root.dataset.askPanel = "open";
    else if (threadWanted) root.dataset.askPanel = "hidden";
    else delete root.dataset.askPanel;
    return () => {
      delete root.dataset.askPanel;
    };
  }, [hydrated, visible, threadWanted]);
}

function usePanelState(disabled: boolean) {
  const openByDefault = useSyncExternalStore(subscribeToWidth, readOpenByDefault, () => false);
  const [chosen, setChosen] = useState<boolean | null>(null);
  const [threadWanted, setThreadWanted] = useState(false);
  const { publicChatMessage, clearPublicChatMessage } = useSearchContextSafe();

  if (!disabled && publicChatMessage && !(threadWanted && chosen === true)) {
    setThreadWanted(true);
    setChosen(true);
  }
  const visible = !disabled && (chosen ?? openByDefault);
  useAskPanelRootFlag(visible, threadWanted);

  useEffect(() => {
    if (disabled && publicChatMessage) clearPublicChatMessage();
  }, [disabled, publicChatMessage, clearPublicChatMessage]);

  const show = useCallback(() => {
    setChosen(true);
    writeHiddenPreference(false);
  }, []);
  const hide = useCallback(() => {
    setChosen(false);
    writeHiddenPreference(true);
  }, []);
  const dismiss = useCallback(() => setChosen(false), []);

  useEffect(() => {
    if (disabled) return;
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, show);
    return () => window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, show);
  }, [disabled, show]);

  return { visible, mounted: visible || chosen !== null, requested: chosen === true, threadWanted, show, hide, dismiss };
}

function DismissOnFailure({ onDismiss }: { onDismiss: () => void }) {
  useEffect(onDismiss, [onDismiss]);
  return null;
}

function AskPanelSlot({ panel }: { panel: ReturnType<typeof usePanelState> }) {
  return (
    <ErrorBoundary
      label="public-ask-panel"
      fallback={
        panel.requested ? (
          <LoadFailureDialog
            title="Ask Repackify could not load"
            open={panel.visible}
            onOpenChange={(open) => {
              if (!open) panel.hide();
            }}
          />
        ) : (
          <DismissOnFailure onDismiss={panel.dismiss} />
        )
      }
    >
      <Suspense fallback={null}>
        <PublicAskPanel
          visible={panel.visible}
          requested={panel.requested}
          threadWanted={panel.threadWanted}
          onHide={panel.hide}
          onDismiss={panel.dismiss}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

export function PublicChatWidget() {
  const viewingAs = useActiveViewAs();
  const panel = usePanelState(Boolean(viewingAs));

  if (viewingAs) return null;

  return (
    <>
      {!panel.visible && (
        <Button variant="chip" size="chip" className="ml-2 shrink-0" onClick={panel.show}>
          <RepackifyMark alt="" className="h-4" />
          <span className="max-sm:sr-only">Ask Repackify</span>
          <span className="sm:hidden" aria-hidden>
            Ask
          </span>
        </Button>
      )}
      {!panel.visible && panel.threadWanted && (
        <Button
          variant="chip"
          size="chip"
          className="fixed bottom-[max(1rem,var(--safe-area-inset-bottom))] left-1/2 z-40 min-h-12 -translate-x-1/2 whitespace-nowrap bg-surface px-5 text-base shadow-modal lg:hidden"
          onClick={panel.show}
        >
          <MessageSquare className="size-[18px]" aria-hidden />
          Back to chat
        </Button>
      )}
      {panel.mounted && <AskPanelSlot panel={panel} />}
    </>
  );
}
